import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Fish,
  MapPin,
  Moon,
  Search,
  ShieldCheck,
  TicketCheck,
  Users,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/Select";

const RADII = [5, 10, 20, 30, 50, 100, 200];
const STALE_DAYS = 180; // warn if access info older than 6 months

function isStale(dateStr: string | null | undefined): boolean {
  if (!dateStr) return true;
  const ms = Date.now() - new Date(dateStr).getTime();
  return ms > STALE_DAYS * 24 * 60 * 60 * 1000;
}

function AccessBadges({ lake }: { lake: any }) {
  const badges: React.ReactNode[] = [];
  if (lake.day_ticket)
    badges.push(
      <span
        key="dt"
        className="inline-flex items-center gap-1 rounded-full bg-success-weak px-2 py-0.5 text-xs font-medium text-success"
      >
        <TicketCheck className="size-3" /> Day ticket
      </span>,
    );
  if (lake.members_only)
    badges.push(
      <span
        key="mo"
        className="inline-flex items-center gap-1 rounded-full bg-info-weak px-2 py-0.5 text-xs font-medium text-info"
      >
        <Users className="size-3" /> Members
      </span>,
    );
  if (lake.user_is_member)
    badges.push(
      <span
        key="mymember"
        className="inline-flex items-center gap-1 rounded-full bg-success-weak px-2 py-0.5 text-xs font-medium text-success"
      >
        My membership ✓
      </span>,
    );
  if (lake.guest_ticket)
    badges.push(
      <span
        key="gt"
        className="inline-flex items-center gap-1 rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary"
      >
        Guest ticket
      </span>,
    );
  if (lake.night_fishing)
    badges.push(
      <span
        key="nf"
        className="inline-flex items-center gap-1 rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary"
      >
        <Moon className="size-3" /> Nights
      </span>,
    );
  if (lake.booking_required)
    badges.push(
      <span
        key="br"
        className="inline-flex items-center gap-1 rounded-full bg-warning-weak px-2 py-0.5 text-xs font-medium text-warning"
      >
        Booking required
      </span>,
    );
  return badges.length ? (
    <div className="mt-2 flex flex-wrap gap-1">{badges}</div>
  ) : null;
}

function SpeciesBadges({ lake }: { lake: any }) {
  return (
    <div className="flex flex-wrap gap-1">
      {lake.contains_carp && (
        <span className="inline-flex items-center gap-1 rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary">
          <Fish className="size-3" /> Carp
        </span>
      )}
      {lake.contains_catfish && (
        <span className="inline-flex items-center gap-1 rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary">
          <Fish className="size-3" /> Catfish
        </span>
      )}
    </div>
  );
}

export default function FindLakePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Detect return-to-advisor context: set by Advisor when user taps "Add a lake"
  // from the Future Trip lake picker. We restore the trip fields via URL params
  // and navigate back after a successful submission.
  const urlParams = new URLSearchParams(window.location.search);
  const returnToAdvisor = urlParams.get("returnToAdvisor") === "1";
  const advisorReturnParams = returnToAdvisor
    ? {
        trip_date: urlParams.get("trip_date") ?? "",
        expected_start_time: urlParams.get("expected_start_time") ?? "05:30",
        expected_duration_hours:
          urlParams.get("expected_duration_hours") ?? "24",
        target_species: urlParams.get("target_species") ?? "carp",
        prefill_name: urlParams.get("prefill_name") ?? "",
      }
    : null;

  // Search state
  const [nameQ, setNameQ] = useState(advisorReturnParams?.prefill_name ?? "");
  const [postcode, setPostcode] = useState("");
  const [radiusMiles, setRadiusMiles] = useState("20");
  const [speciesFilter, setSpeciesFilter] = useState("");
  const [originLat, setOriginLat] = useState<number | null>(null);
  const [originLng, setOriginLng] = useState<number | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState("");
  const [locationUsed, setLocationUsed] = useState<"postcode" | "gps" | ("")>("");

  // Search results
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Add to My Lakes — keyed by canonical_lake_id
  const [adding, setAdding] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [toastMsg, setToastMsg] = useState("");

  // Can't find it — submit new lake
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitPrivate, setSubmitPrivate] = useState<boolean | null>(null);
  const [submitForm, setSubmitForm] = useState({
    name: nameQ,
    venue_name: "",
    postcode: "",
    location: "",
    species: "unknown" as "carp" | "catfish" | "both" | "unknown",
  });
  const [submitLat, setSubmitLat] = useState<number | null>(null);
  const [submitLng, setSubmitLng] = useState<number | null>(null);
  const [submitGpsStatus, setSubmitGpsStatus] = useState<
    "idle" | "loading" | "ready" | "error"
  >("idle");
  // duplicate results shown before final submission
  const [submitDuplicates, setSubmitDuplicates] = useState<any[]>([]);
  const [awaitingChoice, setAwaitingChoice] = useState(false);
  // search-result duplicate warning (unchanged)
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  async function geocodePostcode() {
    if (!postcode.trim()) return null;
    setGeocoding(true);
    setGeocodeError("");
    try {
      const res = await fetch(
        `/app-api/directory/geocode?postcode=${encodeURIComponent(postcode.trim())}`,
      );
      if (!res.ok) {
        setGeocodeError("Postcode not found. Check and try again.");
        setGeocoding(false);
        return null;
      }
      const data = await res.json();
      setOriginLat(data.lat);
      setOriginLng(data.lng);
      setLocationUsed("postcode");
      setGeocoding(false);
      return { lat: data.lat, lng: data.lng };
    } catch {
      setGeocodeError("Could not look up postcode. Check your connection.");
      setGeocoding(false);
      return null;
    }
  }

  async function useCurrentLocation() {
    if (!navigator.geolocation) {
      setGeocodeError("Your browser does not support location access.");
      return;
    }
    setGeocoding(true);
    setGeocodeError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOriginLat(pos.coords.latitude);
        setOriginLng(pos.coords.longitude);
        setLocationUsed("gps");
        setGeocoding(false);
      },
      () => {
        setGeocodeError("Location access was denied or unavailable.");
        setGeocoding(false);
      },
      { timeout: 10000 },
    );
  }

  function useCurrentLocationForSubmission() {
    if (!navigator.geolocation) {
      setSubmitGpsStatus("error");
      setSubmitMessage("Your browser does not support location access.");
      return;
    }
    setSubmitGpsStatus("loading");
    setSubmitMessage("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSubmitLat(pos.coords.latitude);
        setSubmitLng(pos.coords.longitude);
        setSubmitGpsStatus("ready");
      },
      () => {
        setSubmitGpsStatus("error");
        setSubmitMessage(
          "Could not use your current location. Allow location access and try again.",
        );
      },
      { timeout: 10000, enableHighAccuracy: true },
    );
  }

  async function runSearch() {
    setSearching(true);
    setSearchError("");
    setSearched(false);

    let lat = originLat;
    let lng = originLng;

    // If postcode entered but not yet geocoded, do it now
    if (postcode.trim() && (lat === null || locationUsed !== "postcode")) {
      const coords = await geocodePostcode();
      if (coords) {
        lat = coords.lat;
        lng = coords.lng;
      }
    }

    try {
      const params = new URLSearchParams();
      if (nameQ.trim()) params.set("q", nameQ.trim());
      if (lat !== null && lng !== null) {
        params.set("lat", String(lat));
        params.set("lng", String(lng));
        params.set("radius_miles", radiusMiles);
      }
      if (speciesFilter) params.set("species", speciesFilter);
      const res = await fetch(`/app-api/directory/search?${params}`);
      if (!res.ok) throw new Error("Search failed");
      const data: any[] = await res.json();
      setResults(data);
      setSearched(true);
      // Pre-populate addedIds from server-side already_in_my_lakes flag
      const alreadyAdded = new Set<string>(
        data.filter((r) => r.already_in_my_lakes).map((r) => String(r.id)),
      );
      setAddedIds((prev) => new Set([...prev, ...alreadyAdded]));
    } catch {
      setSearchError("Could not search the lake directory. Try again.");
    }
    setSearching(false);
  }

  async function addToMyLakes(canonicalId: string, lakeName: string) {
    if (addedIds.has(canonicalId)) return; // already added — no-op
    setAdding(canonicalId);
    try {
      const res = await fetch("/app-api/directory/add-to-my-lakes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ canonical_lake_id: canonicalId }),
      });
      if (!res.ok) throw new Error("Could not add lake");
      const data = await res.json();
      const name = data.name || lakeName;
      queryClient.invalidateQueries({ queryKey: ["carpintel_lakes"] });
      setAddedIds((prev) => new Set([...prev, canonicalId]));
      const label = data.already_exists
        ? `${name} is already in My Lakes`
        : `${name} added to My Lakes`;
      setToastMsg(label);
      setTimeout(() => setToastMsg(""), 4000);
    } catch {
      // keep UI usable
    }
    setAdding(null);
  }

  async function checkDuplicates(
    name: string,
    lat: number | null,
    lng: number | null,
  ) {
    if (!name.trim()) return;
    try {
      const params = new URLSearchParams({ name });
      if (lat !== null && lng !== null) {
        params.set("lat", String(lat));
        params.set("lng", String(lng));
      }
      const res = await fetch(`/app-api/directory/check-duplicate?${params}`);
      if (res.ok) setDuplicates(await res.json());
    } catch {
      /* non-fatal */
    }
  }

  // Stage 1: geocode postcode + check for duplicates, then await user choice
  async function prepareSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitMessage("");
    setSubmitDuplicates([]);
    setAwaitingChoice(false);

    let lat = submitLat;
    let lng = submitLng;

    // Geocode the submission postcode if provided and not yet resolved
    if (submitForm.postcode.trim() && (lat === null || lng === null)) {
      try {
        const res = await fetch(
          `/app-api/directory/geocode?postcode=${encodeURIComponent(submitForm.postcode.trim())}`,
        );
        if (res.ok) {
          const coords = await res.json();
          lat = coords.lat;
          lng = coords.lng;
          setSubmitLat(lat);
          setSubmitLng(lng);
        }
      } catch {
        /* non-fatal */
      }
    }

    // Check for duplicates using name + optional coordinates
    try {
      const params = new URLSearchParams({ name: submitForm.name });
      if (lat !== null && lng !== null) {
        params.set("lat", String(lat));
        params.set("lng", String(lng));
      }
      const res = await fetch(`/app-api/directory/check-duplicate?${params}`);
      if (res.ok) {
        const dupes: any[] = await res.json();
        if (dupes.length > 0) {
          setSubmitDuplicates(dupes);
          setAwaitingChoice(true);
          return; // pause for user to decide
        }
      }
    } catch {
      /* non-fatal */
    }

    // No duplicates found — proceed directly
    await doSubmit(lat, lng);
  }

  // Stage 2: actually create the lake after user has confirmed "none of these"
  async function doSubmit(lat: number | null, lng: number | null) {
    setSubmitting(true);
    setSubmitMessage("");
    try {
      const res = await fetch("/app-api/directory/submit-lake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: submitForm.name,
          venue_name: submitForm.venue_name || null,
          postcode: submitForm.postcode || null,
          location: submitForm.location || null,
          species: submitForm.species,
          latitude: lat,
          longitude: lng,
          is_private: submitPrivate,
        }),
      });
      if (!res.ok) throw new Error("Could not save lake");
      const result = await res.json();
      queryClient.invalidateQueries({ queryKey: ["carpintel_lakes"] });

      // If we came from the Advisor Future Trip picker, navigate back with the
      // newly added lake details so the Advisor can preselect it.
      if (returnToAdvisor && advisorReturnParams) {
        const back = new URLSearchParams();
        back.set("returnToAdvisor", "1");
        if (advisorReturnParams.trip_date)
          back.set("trip_date", advisorReturnParams.trip_date);
        back.set(
          "expected_start_time",
          advisorReturnParams.expected_start_time,
        );
        back.set(
          "expected_duration_hours",
          advisorReturnParams.expected_duration_hours,
        );
      back.set("target_species", advisorReturnParams.target_species);
      // Pass the canonical_lake_id (for community submissions) or personal lake id
      if (result.canonical_lake_id) {
        back.set("new_canonical_id", String(result.canonical_lake_id));
      }
      back.set("new_lake_name", submitForm.name);
      navigate(`/advisor?${back.toString()}`);
      return;
    }

    setSubmitSuccess(
      submitPrivate
        ? `${submitForm.name} added to My Lakes`
        : `${submitForm.name} submitted for review and added to My Lakes`,
    );
    setShowSubmit(false);
    setAwaitingChoice(false);
    setSubmitPrivate(null);
    setSubmitLat(null);
    setSubmitLng(null);
    setSubmitGpsStatus("idle");
    setSubmitForm({
      name: "",
      venue_name: "",
      postcode: "",
      location: "",
      species: "unknown",
    });
  } catch {
    setSubmitMessage("Could not save lake. Try again.");
  }
  setSubmitting(false);
}

  // Pre-open the submit form if arriving from Advisor with a prefilled lake name
  useEffect(() => {
    if (advisorReturnParams?.prefill_name) {
      setShowSubmit(true);
      setSubmitForm((f) => ({
        ...f,
        name: advisorReturnParams.prefill_name,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      {toastMsg ? (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-32 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-sm font-medium text-background shadow-lg"
        >
          {toastMsg}
        </div>
      ) : null}
      <header>
        <Link
          to="/lakes"
          className="text-sm font-medium text-accent hover:underline"
        >
          My lakes
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-primary">
          Find a lake
        </h1>
        <p className="mt-1 text-sm text-secondary">
          Search {""} 100+ curated waters. Add any to your personal lake log.
        </p>
      </header>

      {/* Submit success confirmation */}
      {submitSuccess ? (
        <div
          role="status"
          className="rounded-lg border border-success bg-success-weak p-4 text-sm font-medium text-success"
        >
          {submitSuccess}
        </div>
      ) : null}

      {/* Search controls */}
      <div className="space-y-3 rounded-lg border border-border bg-raised p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label
              className="mb-1 block text-sm font-medium text-primary"
              htmlFor="find-name"
            >
              Lake or venue name
            </label>
            <Input
              id="find-name"
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
              placeholder="e.g. Yateley, Todber, Bluebell"
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
            />
          </div>
          <div>
            <label
              className="mb-1 block text-sm font-medium text-primary"
              htmlFor="find-species"
            >
              Species
            </label>
            <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
              <SelectTrigger id="find-species">
                <SelectValue placeholder="Any species" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any species</SelectItem>
                <SelectItem value="carp">Carp</SelectItem>
                <SelectItem value="catfish">Catfish</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <label
              className="mb-1 block text-sm font-medium text-primary"
              htmlFor="find-postcode"
            >
              UK postcode
            </label>
            <div className="flex gap-2">
              <Input
                id="find-postcode"
                value={postcode}
                onChange={(e) => {
                  setPostcode(e.target.value);
                  setOriginLat(null);
                  setOriginLng(null);
                  setLocationUsed("");
                }}
                placeholder="e.g. RG27 8BU"
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={useCurrentLocation}
                isLoading={geocoding}
                aria-label="Use current location"
              >
                <MapPin />
                <span className="hidden sm:inline">My location</span>
              </Button>
            </div>
            {geocodeError ? (
              <p className="mt-1 text-xs text-error">{geocodeError}</p>
            ) : locationUsed === "gps" ? (
              <p className="mt-1 text-xs text-success">
                Using your current location.
              </p>
            ) : locationUsed === "postcode" ? (
              <p className="mt-1 text-xs text-success">Postcode resolved.</p>
            ) : null}
          </div>
          <div>
            <label
              className="mb-1 block text-sm font-medium text-primary"
              htmlFor="find-radius"
            >
              Within
            </label>
            <Select value={radiusMiles} onValueChange={setRadiusMiles}>
              <SelectTrigger id="find-radius">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RADII.map((r) => (
                  <SelectItem key={r} value={String(r)}>
                    {r} miles
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={runSearch} isLoading={searching} size="lg">
            <Search /> Search lakes
          </Button>
          {searched && results.length > 0 && (
            <p className="text-sm text-secondary">
              {results.length} {results.length === 1 ? "lake" : "lakes"} found
              {originLat !== null ? ` within ${radiusMiles} miles` : ""}
            </p>
          )}
        </div>
        {searchError ? (
          <p className="text-sm text-error">{searchError}</p>
        ) : null}
      </div>

      {/* Results */}
      {searched && results.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-base font-semibold text-primary">No lakes found</p>
          <p className="mt-2 text-sm text-secondary">
            Try a different name, postcode or radius, or submit a new lake
            below.
          </p>
          <Button
            className="mt-4"
            onClick={() => {
              setShowSubmit(true);
              setSubmitForm((f) => ({ ...f, name: nameQ }));
            }}
          >
            Can't find it? Add a lake
          </Button>
        </div>
      ) : searched ? (
        <div className="space-y-3">
          {results.map((lake: any) => {
            const added = addedIds.has(lake.id);
            const stale = isStale(lake.access_last_verified_at);
            return (
              <div
                key={lake.id}
                className="rounded-lg border border-border bg-raised p-4"
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <h3 className="truncate text-base font-semibold text-primary">
                        {lake.canonical_name}
                      </h3>
                      {lake.verification_status === "venue_verified" && (
                        <ShieldCheck
                          className="size-4 shrink-0 text-success"
                          aria-label="Verified"
                        />
                      )}
                    </div>
                    {lake.venue_name ? (
                      <p className="mt-0.5 truncate text-sm text-secondary">
                        {lake.venue_name}
                        {lake.county_region ? ` · ${lake.county_region}` : ""}
                      </p>
                    ) : lake.county_region ? (
                      <p className="mt-0.5 text-sm text-secondary">
                        {lake.county_region}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    {lake.distance_miles !== undefined && (
                      <span className="text-sm font-medium text-secondary">
                        {lake.distance_miles} mi
                      </span>
                    )}
                    {added ? (
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-success">
                        <CheckCircle className="size-4" /> In My Lakes
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() =>
                          addToMyLakes(lake.id, lake.canonical_name)
                        }
                        isLoading={adding === lake.id}
                      >
                        Add to My Lakes
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <SpeciesBadges lake={lake} />
                  {lake.provider_names && (
                    <span className="text-xs text-secondary">
                      {lake.provider_names}
                    </span>
                  )}
                </div>

                <AccessBadges lake={lake} />

                {stale && (lake.day_ticket || lake.members_only) && (
                  <p className="mt-2 flex items-center gap-1 text-xs text-warning">
                    <AlertTriangle className="size-3 shrink-0" />
                    Access information may be outdated — check before
                    travelling.
                  </p>
                )}
              </div>
            );
          })}

          {/* Can't find it prompt */}
          {!showSubmit && (
            <div className="rounded-lg border border-dashed border-border p-4 text-center">
              <p className="text-sm text-secondary">
                Can't find your lake in these results?
              </p>
              <Button
                variant="tertiary"
                className="mt-2"
                onClick={() => {
                  setShowSubmit(true);
                  setSubmitForm((f) => ({ ...f, name: nameQ }));
                  checkDuplicates(nameQ, originLat, originLng);
                }}
              >
                Add a lake not in the directory{" "}
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </div>
      ) : null}

      {/* Can't find it — submit new lake */}
      {showSubmit && (
        <section className="space-y-4 rounded-lg border border-border bg-raised p-4">
          <h2 className="text-lg font-semibold text-primary">Add a lake</h2>

          {/* Duplicate warning */}
          {duplicates.length > 0 && (
            <div className="rounded-md border border-warning bg-warning-weak p-3">
              <p className="text-sm font-medium text-warning">
                Similar lakes already exist in the directory:
              </p>
              <ul className="mt-2 space-y-1">
                {duplicates.map((d: any) => (
                  <li key={d.id} className="text-sm text-warning">
                    {d.canonical_name}
                    {d.county_region ? ` — ${d.county_region}` : ""}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-warning">
                Check the results above before adding a duplicate.
              </p>
            </div>
          )}

          {/* Privacy choice */}
          {submitPrivate === null ? (
            <div className="space-y-3">
              <p className="text-sm text-secondary">
                How should this lake be added?
              </p>
              {/* When arriving from Future Trip, private lakes cannot be used
                  for advice (no canonical ID). Show only Community submitted
                  and explain why Private to me is unavailable in this context. */}
              {returnToAdvisor ? (
                <>
                  <div className="rounded-md border border-info bg-info-weak px-3 py-2.5">
                    <p className="text-xs text-info">
                      Future Trip advice requires a directory lake. To keep a
                      lake private, add it separately from My Lakes.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSubmitPrivate(false)}
                    className="w-full rounded-lg border border-border bg-raised p-4 text-left transition-colors hover:bg-inset"
                  >
                    <p className="text-sm font-semibold text-primary">
                      Community submitted
                    </p>
                    <p className="mt-1 text-xs text-secondary">
                      Added to your personal log and submitted for inclusion in
                      the public directory (pending review). Helps other
                      anglers.
                    </p>
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setSubmitPrivate(false)}
                    className="rounded-lg border border-border bg-raised p-4 text-left transition-colors hover:bg-inset"
                  >
                    <p className="text-sm font-semibold text-primary">
                      Community submitted
                    </p>
                    <p className="mt-1 text-xs text-secondary">
                      Added to your personal log and submitted for inclusion in
                      the public directory (pending review). Helps other
                      anglers.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSubmitPrivate(true)}
                    className="rounded-lg border border-border bg-raised p-4 text-left transition-colors hover:bg-inset"
                  >
                    <p className="text-sm font-semibold text-primary">
                      Private to me
                    </p>
                    <p className="mt-1 text-xs text-secondary">
                      Added to your personal log only. Never enters the public
                    directory or community statistics.
                  </p>
                </button>
              </div>
            )}
          </div>
        ) : awaitingChoice ? (
          // Stage 2a: duplicates found — let user confirm before submitting
          <div className="space-y-4">
            <div className="rounded-md border border-warning bg-warning-weak p-3">
              <p className="text-sm font-semibold text-warning">
                Could this be one of these waters?
              </p>
              <ul className="mt-2 space-y-2">
                {submitDuplicates.map((d: any) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-sm text-warning">
                      {d.canonical_name}
                      {d.county_region ? ` — ${d.county_region}` : ""}
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={async () => {
                        await addToMyLakes(d.id, d.canonical_name);
                        setShowSubmit(false);
                        setAwaitingChoice(false);
                        setSubmitPrivate(null);
                      }}
                    >
                      Add this lake
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setAwaitingChoice(false);
                  doSubmit(submitLat, submitLng);
                }}
                isLoading={submitting}
              >
                None of these — submit new lake
              </Button>
              <Button
                variant="tertiary"
                onClick={() => {
                  setAwaitingChoice(false);
                  setShowSubmit(false);
                  setSubmitPrivate(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          // Stage 1: fill out the form
          <form onSubmit={prepareSubmit} className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary">
                {submitPrivate ? "Private" : "Community submission"}
              </span>
              <button
                type="button"
                className="text-xs text-accent hover:underline"
                onClick={() => setSubmitPrivate(null)}
              >
                Change
              </button>
            </div>

            <div>
              <label
                className="mb-1 block text-sm font-medium text-primary"
                htmlFor="submit-name"
              >
                Lake name <span className="text-error">*</span>
              </label>
              <Input
                id="submit-name"
                value={submitForm.name}
                onChange={(e) =>
                  setSubmitForm((f) => ({ ...f, name: e.target.value }))
                }
                required
              />
            </div>

            {!submitPrivate && (
              <div>
                <label
                  className="mb-1 block text-sm font-medium text-primary"
                  htmlFor="submit-venue"
                >
                  Venue / fishery name
                </label>
                <Input
                  id="submit-venue"
                  value={submitForm.venue_name}
                  onChange={(e) =>
                    setSubmitForm((f) => ({
                      ...f,
                      venue_name: e.target.value,
                    }))
                  }
                  placeholder="e.g. Todber Manor, Bluebell Lakes"
                />
              </div>
            )}

            <div>
              <label
                className="mb-1 block text-sm font-medium text-primary"
                htmlFor="submit-postcode"
              >
                UK postcode
              </label>
              <Input
                id="submit-postcode"
                value={submitForm.postcode}
                onChange={(e) => {
                  setSubmitForm((f) => ({ ...f, postcode: e.target.value }));
                  setSubmitLat(null);
                  setSubmitLng(null);
                  setSubmitGpsStatus("idle");
                }}
                placeholder="e.g. GU46 7UU"
              />
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={useCurrentLocationForSubmission}
                  isLoading={submitGpsStatus === "loading"}
                >
                  <MapPin /> Use my current location for this lake
                </Button>
                {submitGpsStatus === "ready" ? (
                  <p className="mt-1 text-xs text-success">
                    Lake location captured. Weather can use these coordinates.
                  </p>
                ) : submitGpsStatus === "error" ? (
                  <p className="mt-1 text-xs text-error">
                    Location not captured. You can try again or save without it.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-secondary">
                    Useful outside the UK or when the lake is not in our directory yet.
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                className="mb-1 block text-sm font-medium text-primary"
                htmlFor="submit-location"
              >
                Location / town (optional)
              </label>
              <Input
                id="submit-location"
                value={submitForm.location}
                onChange={(e) =>
                  setSubmitForm((f) => ({ ...f, location: e.target.value }))
                }
                placeholder="Town or county"
              />
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-primary">
                Species present
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {(["carp", "catfish", "both", "unknown"] as const).map(
                  (s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() =>
                        setSubmitForm((f) => ({ ...f, species: s }))
                      }
                      className={`rounded-lg border px-3 py-2 text-sm font-medium capitalize transition-colors ${
                        submitForm.species === s
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-border bg-raised text-secondary hover:bg-inset"
                      }`}
                    >
                      {s}
                    </button>
                  ),
                )}
              </div>
            </div>

            {submitMessage ? (
              <p className="text-sm text-error">{submitMessage}</p>
            ) : null}
            <div className="flex gap-3">
              <Button type="submit" isLoading={submitting}>
                Check and save
              </Button>
              <Button
                type="button"
                variant="tertiary"
                onClick={() => {
                  setShowSubmit(false);
                  setSubmitPrivate(null);
                  setAwaitingChoice(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </section>
    )}
  </div>
  );
}

